import { legacy_createStore as createStore, StoreEnhancer } from "redux";

import { State } from "./types/state";
import { Selector } from "./types/selector";
import { combineStateReducer } from "./utils/combineStateReducer";
import * as account from "./account";
import * as allocations from "./allocations";
import * as comments from "./comments";
import * as preferences from "./preferences";
import * as appNotifications from "./appNotifications";
import * as permissions from "./permissions";
import * as planzone from "./planzone";
import * as projectExternalLinks from "./projectExternalLinks";
import * as projectManagementPlan from "./projectManagementPlan";
import * as projectReports from "./projectReports";
import * as projects from "./projects";
import * as projectStatuses from "./projectStatuses";
import * as resourceGroups from "./resourceGroups";
import * as resources from "./resources";
import * as tags from "./tags";
import * as zones from "./zones";
import * as projectPortfolio from "./projectPortfolio";
import * as notifications from "./notifications";
import * as documents from "./documents";
import * as search from "./search";
import * as timeSheets from "./timeSheets";
import * as budget from "./budget";
import * as workLoads from "./workLoads";
import * as constraints from "./constraints";
import * as unavailability from "./unavailability";
import * as dataExports from "./dataExports";
import * as strategicAxes from "./strategicAxes";
import * as calendarExportLink from "./calendarExportLink";
import * as videoConferenceCall from "./videoConferenceCall";
import * as secondaryCriteria from "./secondaryCriteria";
import * as calendarHolidays from "./calendarHolidays";
import * as logs from "./logs";
import * as capacityPlanning from "./capacityPlanning";
import * as chatbot from "./chatbot";
import * as organizationalUnits from "./organizationalUnits";

const initialState: State = {
  projects: projects.initialState,
  projectExternalLinks: projectExternalLinks.initialState,
  comments: comments.initialState,
  permissions: permissions.initialState,
  allocations: allocations.initialState,
  appNotifications: appNotifications.initialState,
  tags: tags.initialState,
  resourceGroups: resourceGroups.initialState,
  projectStatuses: projectStatuses.initialState,
  projectManagementPlan: projectManagementPlan.initialState,
  projectReports: projectReports.initialState,
  account: account.initialState,
  resources: resources.initialState,
  preferences: preferences.initialState,
  planzone: planzone.initialState,
  zones: zones.initialState,
  projectPortfolio: projectPortfolio.initialState,
  notifications: notifications.initialState,
  documents: documents.initialState,
  search: search.initialState,
  timeSheets: timeSheets.initialState,
  budget: budget.initialState,
  workLoads: workLoads.initialState,
  constraints: constraints.initialState,
  unavailability: unavailability.initialState,
  dataExports: dataExports.initialState,
  strategicAxes: strategicAxes.initialState,
  calendarExportLink: calendarExportLink.initialState,
  videoConferenceCall: videoConferenceCall.initialState,
  secondaryCriteria: secondaryCriteria.initialState,
  calendarHolidays: calendarHolidays.initialState,
  logs: logs.initialState,
  capacityPlanning: capacityPlanning.initialState,
  chatbot: chatbot.initialState,
  organizationalUnits: organizationalUnits.initialState,
};

const reduceState = combineStateReducer(initialState, {
  projects: projects.reduceState,
  projectExternalLinks: projectExternalLinks.reduceState,
  comments: comments.reduceState,
  permissions: permissions.reduceState,
  allocations: allocations.reduceState,
  appNotifications: appNotifications.reduceState,
  tags: tags.reduceState,
  resourceGroups: resourceGroups.reduceState,
  projectStatuses: projectStatuses.reduceState,
  projectManagementPlan: projectManagementPlan.reduceState,
  projectReports: projectReports.reduceState,
  account: account.reduceState,
  resources: resources.reduceState,
  preferences: preferences.reduceState,
  planzone: planzone.reduceState,
  zones: zones.reduceState,
  projectPortfolio: projectPortfolio.reduceState,
  notifications: notifications.reduceState,
  documents: documents.reduceState,
  search: search.reduceState,
  timeSheets: timeSheets.reduceState,
  budget: budget.reduceState,
  workLoads: workLoads.reduceState,
  constraints: constraints.reduceState,
  unavailability: unavailability.reduceState,
  dataExports: dataExports.reduceState,
  strategicAxes: strategicAxes.reduceState,
  calendarExportLink: calendarExportLink.reduceState,
  videoConferenceCall: videoConferenceCall.reduceState,
  secondaryCriteria: secondaryCriteria.reduceState,
  calendarHolidays: calendarHolidays.reduceState,
  logs: logs.reduceState,
  capacityPlanning: capacityPlanning.reduceState,
  chatbot: chatbot.reduceState,
  organizationalUnits: organizationalUnits.reduceState,
});

function createSelector(getState: () => State): Selector {
  return {
    ...account.createSelector(() => getState().account),
    ...allocations.createSelector(() => getState().allocations),
    ...comments.createSelector(() => getState().comments),
    ...preferences.createSelector(() => getState().preferences),
    ...appNotifications.createSelector(() => getState().appNotifications),
    ...permissions.createSelector(() => getState().permissions),
    ...planzone.createSelector(() => getState().planzone),
    ...projectExternalLinks.createSelector(
      () => getState().projectExternalLinks,
    ),
    ...projectManagementPlan.createSelector(
      () => getState().projectManagementPlan,
    ),
    ...projectReports.createSelector(() => getState().projectReports),
    ...projects.createSelector(() => getState().projects),
    ...projectStatuses.createSelector(() => getState().projectStatuses),
    ...resourceGroups.createSelector(() => getState().resourceGroups),
    ...resources.createSelector(() => getState().resources),
    ...tags.createSelector(() => getState().tags),
    ...zones.createSelector(() => getState().zones),
    ...projectPortfolio.createSelector(() => getState().projectPortfolio),
    ...notifications.createSelector(() => getState().notifications),
    ...documents.createSelector(() => getState().documents),
    ...search.createSelector(() => getState().search),
    ...timeSheets.createSelector(() => getState().timeSheets),
    ...budget.createSelector(() => getState().budget),
    ...workLoads.createSelector(() => getState().workLoads),
    ...constraints.createSelector(() => getState().constraints),
    ...unavailability.createSelector(() => getState().unavailability),
    ...dataExports.createSelector(() => getState().dataExports),
    ...strategicAxes.createSelector(() => getState().strategicAxes),
    ...calendarExportLink.createSelector(() => getState().calendarExportLink),
    ...videoConferenceCall.createSelector(() => getState().videoConferenceCall),
    ...secondaryCriteria.createSelector(() => getState().secondaryCriteria),
    ...calendarHolidays.createSelector(() => getState().calendarHolidays),
    ...logs.createSelector(() => getState().logs),
    ...capacityPlanning.createSelector(() => getState().capacityPlanning),
    ...chatbot.createSelector(() => getState().chatbot),
    ...organizationalUnits.createSelector(() => getState().organizationalUnits),
  };
}

function createState(middleware?: StoreEnhancer) {
  return createStore(reduceState, middleware);
}

export type { State, Selector };

export { reduceState, initialState, createSelector, createState };
